import React from 'react';
import { Box, Unstable_Grid2 as Grid } from '@mui/material';
import { Form, FormRenderProps } from 'react-final-form';
import LoadingButton from '@mui/lab/LoadingButton';

import {
  Button,
  Card,
  CardContent,
  Select,
  TextField,
  TextFieldProps,
  Typography,
} from '@occc-app/design-system';
import { parseValueToOnlyDigits } from '@occc/utils';
import { Cpg } from '@occc/graphql';

import {
  ScanDataSubmissionGroupsUpdateFormValues,
  ScanDataSubmissionGroupsUpdateFormValuesPaths,
} from './scan-data-submission-groups-update.types';
import { submissionTypeOptions } from './scan-data-submission-groups-update.constants';
import { validateScanDataSubmissionGroupUpdate } from './scan-data-submission-groups-update.validation';
import { TestAndUpdateSFTPCredentialsStatusModal } from './components/test-and-update-status.modal';
import { TobaccoCompanyWithConfirmation } from './components/tobacco-company-with-confirmation.component';
import { SubmitterIdWithConfirmation } from './components/submitter-id-with-confirmation.component';

type ScanDataSubmissionGroupsUpdateProps = {
  initialValues: ScanDataSubmissionGroupsUpdateFormValues;
  onSubmit: (value: ScanDataSubmissionGroupsUpdateFormValues) => void;
  onCancel: () => void;
  onSave: () => void;
  isSftpTestSuccessful: boolean;
  isErrorHappened: boolean;
  showUpdateStatusModal: boolean;
  onUpdateStatusModalCancel: () => void;
};

const onlyDigitsFieldFieldProps: TextFieldProps['fieldProps'] = {
  parse: parseValueToOnlyDigits,
};

export const ScanDataSubmissionGroupsUpdate = ({
  initialValues,
  onSubmit,
  onCancel,
  onSave,
  isSftpTestSuccessful,
  isErrorHappened,
  showUpdateStatusModal,
  onUpdateStatusModalCancel,
}: ScanDataSubmissionGroupsUpdateProps) => {
  const renderForm = React.useCallback(
    ({
      handleSubmit,
      submitting,
      values,
      initialValues,
    }: FormRenderProps<ScanDataSubmissionGroupsUpdateFormValues>) => {
      const cpgWasChanged = values.tobaccoCompany !== initialValues?.tobaccoCompany;
      const submitterIdWasChanged =
        values.sftpDetails.submitterID !== initialValues?.sftpDetails?.submitterID;

      return (
        <form onSubmit={handleSubmit}>
          <Card sx={{ mb: 1 }}>
            <CardContent>
              <Typography variant='h6' mb={2}>
                Group Details
              </Typography>

              <Grid container spacing={3}>
                <Grid xs={12} md={3}>
                  <TextField name='name' label='Submission Group Name' margin='none' />
                </Grid>

                <Grid xs={12} md={3}>
                  <TobaccoCompanyWithConfirmation
                    currentCpg={initialValues?.tobaccoCompany as Cpg}
                    cpgWasChanged={cpgWasChanged}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <Select
                    name={'submissionType' satisfies ScanDataSubmissionGroupsUpdateFormValuesPaths}
                    label='Submission Type'
                    data={submissionTypeOptions}
                    formControlProps={{ size: 'medium' }}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <SubmitterIdWithConfirmation
                    cpg={values.tobaccoCompany}
                    cpgWasChanged={cpgWasChanged}
                    submitterIdWasChanged={submitterIdWasChanged}
                  />
                </Grid>
              </Grid>

              <Typography variant='h6' mb={2} mt={4}>
                SFTP Details
              </Typography>

              <Grid container spacing={3}>
                <Grid xs={12} md={3}>
                  <TextField
                    name={
                      'sftpDetails.username' satisfies ScanDataSubmissionGroupsUpdateFormValuesPaths
                    }
                    label='Username'
                    margin='none'
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <TextField
                    name={
                      'sftpDetails.password' satisfies ScanDataSubmissionGroupsUpdateFormValuesPaths
                    }
                    label='Password'
                    margin='none'
                    type='password'
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <TextField
                    name={
                      'sftpDetails.portNumber' satisfies ScanDataSubmissionGroupsUpdateFormValuesPaths
                    }
                    label='Port Number'
                    margin='none'
                    fieldProps={onlyDigitsFieldFieldProps}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <TextField
                    name={
                      'sftpDetails.submissionURL' satisfies ScanDataSubmissionGroupsUpdateFormValuesPaths
                    }
                    label='Submission URL'
                    margin='none'
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Box display='flex' justifyContent='flex-end' sx={{ mt: 1 }}>
            <Button role='button' onClick={onCancel} sx={{ mr: 3 }}>
              Cancel
            </Button>

            <LoadingButton
              loading={submitting}
              variant='contained'
              color='primary'
              type='submit'
              disabled={submitting}
            >
              Save
            </LoadingButton>
          </Box>

          <TestAndUpdateSFTPCredentialsStatusModal
            show={showUpdateStatusModal}
            onCancel={onUpdateStatusModalCancel}
            onSave={onSave}
            isSftpTestSuccessful={isSftpTestSuccessful}
            isErrorHappened={isErrorHappened}
          />
        </form>
      );
    },
    [
      isSftpTestSuccessful,
      isErrorHappened,
      onCancel,
      onSave,
      onUpdateStatusModalCancel,
      showUpdateStatusModal,
    ],
  );

  return (
    <Form<ScanDataSubmissionGroupsUpdateFormValues>
      onSubmit={onSubmit}
      render={renderForm}
      initialValues={initialValues}
      validate={validateScanDataSubmissionGroupUpdate}
    />
  );
};
